/* eslint-disable react/no-array-index-key */
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { m, AnimatePresence } from 'framer-motion';
import {
    Box, Typography, Stack, TextField, Button, IconButton,
    Tooltip, alpha, Collapse,
} from '@mui/material';
import Iconify from 'src/components/Iconify';

// ─── Phase helpers ───────────────────────────────────────────────────────────

function createPhase(order = 1) {
    return {
        id: uuidv4(),
        name: '',
        description: '',
        estimatedDate: '',
        duration: '',
        order,
    };
}

// ─── PhaseCard ───────────────────────────────────────────────────────────────

function PhaseCard({ phase, index, total, onChange, onRemove }) {
    const [expanded, setExpanded] = useState(true);

    const handleField = (field, value) => {
        onChange({ ...phase, [field]: value });
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px', fontSize: '13px', bgcolor: '#fff',
            '& fieldset': { borderColor: 'rgba(145,158,171,0.2)' },
            '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
        },
        '& .MuiOutlinedInput-input': { py: '8px', px: '10px' },
    };

    return (
        <m.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
        >
            <Box
                sx={{
                    position: 'relative',
                    pl: 4,
                    pb: index < total - 1 ? 3 : 1,
                    '&::before': index < total - 1 ? {
                        content: '""',
                        position: 'absolute',
                        left: 11,
                        top: 28,
                        bottom: 0,
                        width: 2,
                        bgcolor: alpha('#00AB55', 0.15),
                    } : {},
                }}
            >
                {/* Timeline dot */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: 4,
                        top: 8,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: phase.name ? '#00AB55' : '#DFE3E8',
                        border: '2px solid #fff',
                        boxShadow: phase.name ? `0 0 0 2px ${alpha('#00AB55', 0.2)}` : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Typography sx={{ fontSize: '8px', fontWeight: 700, color: phase.name ? '#fff' : '#919EAB' }}>
                        {index + 1}
                    </Typography>
                </Box>

                {/* Phase card */}
                <Box
                    sx={{
                        borderRadius: '10px',
                        border: '1px solid rgba(145,158,171,0.12)',
                        bgcolor: '#fff',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header row */}
                    <Box
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(145,158,171,0.08)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(145,158,171,0.04)';
                        }}
                        onClick={() => setExpanded((v) => !v)}
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            px: 2, py: 1.25, cursor: 'pointer',
                            bgcolor: 'rgba(145,158,171,0.04)',
                            transition: 'background-color 0.2s ease',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '12px', color: '#212B36' }}>
                                {phase.name || `Phase ${index + 1}`}
                                {phase.estimatedDate && (
                                    <Typography component="span" sx={{ fontSize: '11px', color: '#919EAB', ml: 1, fontWeight: 400 }}>
                                        {phase.estimatedDate}
                                    </Typography>
                                )}
                            </Typography>
                            {phase.duration && (
                                <Typography sx={{ fontSize: '11px', color: '#919EAB', mt: 0.25 }}>
                                    Duration: {phase.duration}
                                </Typography>
                            )}
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Tooltip title="Remove phase">
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                                    sx={{ p: 0.25, color: '#C4CDD5', '&:hover': { color: '#FF4842' } }}
                                >
                                    <Iconify icon="eva:trash-2-fill" width={14} />
                                </IconButton>
                            </Tooltip>
                            <Iconify icon={expanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={16} sx={{ color: '#C4CDD5' }} />
                        </Stack>
                    </Box>

                    {/* Phase fields */}
                    <Collapse in={expanded}>
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Phase name
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="e.g. Initial consultation, Implant placement, Healing period..."
                                        value={phase.name}
                                        onChange={(e) => handleField('name', e.target.value)}
                                        sx={inputSx}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Estimated date
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="date"
                                        value={phase.estimatedDate}
                                        onChange={(e) => handleField('estimatedDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={inputSx}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Duration
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="e.g. 2 weeks, 3 months"
                                        value={phase.duration}
                                        onChange={(e) => handleField('duration', e.target.value)}
                                        sx={inputSx}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Description
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        placeholder="What happens during this phase..."
                                        value={phase.description}
                                        onChange={(e) => handleField('description', e.target.value)}
                                        sx={{
                                            ...inputSx,
                                            '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], alignItems: 'flex-start' },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
            </Box>
        </m.div>
    );
}

PhaseCard.propTypes = {
    phase: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

// ─── Main export ─────────────────────────────────────────────────────────────

export default function TreatmentPlanTimeline({ phases, onPhasesChange }) {
    const addPhase = () => {
        const newPhase = createPhase(phases.length + 1);
        onPhasesChange([...phases, newPhase]);
    };

    const updatePhase = (phaseIdx, updatedPhase) => {
        const next = [...phases];
        next[phaseIdx] = updatedPhase;
        onPhasesChange(next);
    };

    const removePhase = (phaseIdx) => {
        onPhasesChange(phases.filter((_, i) => i !== phaseIdx));
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Iconify icon="eva:calendar-fill" width={20} sx={{ color: '#00AB55' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px' }}>
                        Treatment Timeline
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                    Add treatment phases so the patient knows what to expect and when
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '11px', display: 'block', mt: 0.5 }}>
                    This step is optional — skip it if the treatment is straightforward
                </Typography>
            </Box>

            {/* Phases container */}
            <Box sx={{
                bgcolor: '#fff', borderRadius: '14px',
                border: '1px solid rgba(145,158,171,0.16)',
                p: 2.5,
            }}>
                {phases.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Box sx={{
                            width: 48, height: 48, borderRadius: '50%',
                            bgcolor: 'rgba(0,171,85,0.08)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            mx: 'auto', mb: 1.5,
                        }}>
                            <Iconify icon="eva:calendar-fill" width={22} sx={{ color: '#00AB55' }} />
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#637381', mb: 2 }}>
                            No treatment phases yet
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Iconify icon="eva:plus-fill" width={14} />}
                            onClick={addPhase}
                            sx={{
                                textTransform: 'none', fontWeight: 600, fontSize: '12px',
                                borderRadius: '8px', borderColor: alpha('#00AB55', 0.3), color: '#00AB55',
                                '&:hover': { borderColor: '#00AB55', bgcolor: alpha('#00AB55', 0.04) },
                            }}
                        >
                            Add first phase
                        </Button>
                    </Box>
                ) : (
                    <Box>
                        <AnimatePresence>
                            {phases.map((phase, idx) => (
                                <PhaseCard
                                    key={phase.id}
                                    phase={phase}
                                    index={idx}
                                    total={phases.length}
                                    onChange={(updated) => updatePhase(idx, updated)}
                                    onRemove={() => removePhase(idx)}
                                />
                            ))}
                        </AnimatePresence>

                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Iconify icon="eva:plus-fill" width={14} />}
                            onClick={addPhase}
                            sx={{
                                mt: 1, ml: 4,
                                textTransform: 'none', fontWeight: 600, fontSize: '12px',
                                borderRadius: '8px', borderColor: 'rgba(145,158,171,0.3)', color: '#637381',
                                '&:hover': { borderColor: '#00AB55', color: '#00AB55', bgcolor: alpha('#00AB55', 0.04) },
                            }}
                        >
                            Add phase
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

TreatmentPlanTimeline.propTypes = {
    phases: PropTypes.array.isRequired,
    onPhasesChange: PropTypes.func.isRequired,
};
